import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { LUCIDE_ICONS, LucideAngularModule, LucideIconProvider, icons } from 'lucide-angular';
import { Store } from '@ngrx/store';
import { Router, RouterModule } from '@angular/router';
import { CognitoService } from '../../services/cognito.service';
import { User } from '../../models/user';
import { AuthService } from '../../services/auth.service';
import { AuthError } from 'aws-amplify/auth';
import { NotificationService } from '../../services/notification.service';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-register-development',
  standalone: true,
  imports: [LucideAngularModule, ReactiveFormsModule, FormsModule, RouterModule, CommonModule],
  templateUrl: './register-development.component.html',
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }]
})
export class RegisterDevelopmentComponent implements OnInit {

  // Login Form
  signupForm!: UntypedFormGroup;
  loading = false;
  successmsg = false;
  error = '';
  // set the current year
  year: number = new Date().getFullYear();

  fieldTextType!: boolean;
  submitted = false;

  constructor(private formBuilder: UntypedFormBuilder, public store: Store, public cognitoService: CognitoService,
    public auth: AuthService, public router: Router, private notificationService: NotificationService,
    private traslateService: TranslateService) { }

  ngOnInit(): void {
    /**
     * Form Validation
     */
    this.signupForm = this.formBuilder.group({
      nit: ['', [Validators.required]],
      name: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      phone: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
      email: ['', Validators.required, Validators.email],
      password: ['', Validators.required, Validators.minLength(6)],
      confirmPassword: ['', Validators.required],
    }, { validators: this.passwordsMatchValidator });
  }

  get f() { return this.signupForm.controls; }

  async onSubmit() {
    this.loading = false;
    this.submitted = true;

    const nit = this.f['nit'].value;
    const name = this.f['name'].value;
    const lastName = this.f['lastName'].value;
    const phone = this.f['phone'].value;
    let email = this.f['email'].value;
    const password = this.f['password'].value;

    if (this.signupForm.invalid) {
      return
    }

    if (!this.validatePass(password)) {
      const title = await this.traslateService.get('NOTIFI.titleWarning').toPromise();
      const detail = await this.traslateService.get('ERRORS.PassInvalid').toPromise();
      this.notificationService.showWarning(title, detail)
      return
    }

    if (typeof email === 'string') {
      email = email.trim().toLowerCase();
    }

    const isEmail = /^[a-zA-Z0-9.!#$%&*+/=?^_`{|}~-]+@[a-zA-Z0-9~-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email!.toString());
    if (isEmail) {
      this.loading = true;
      this.registerUser(nit!.replace(/\s/g, ''), name!.replace(/\s/g, ''), lastName!.replace(/\s/g, ''), email!.replace(/\s/g, ''), password!.replace(/\s/g, ''), phone!);
    } else {
      const title = await this.traslateService.get('NOTIFI.titleWarning').toPromise();
      const detail = await this.traslateService.get('ERRORS.emailError').toPromise();
      this.notificationService.showWarning(title, detail)
      this.loading = false;
    }
  }

  async registerUser(nit: string, name: string, lastName: string, email: string, password: string, phone: number) {
    try {
      //await this.cognitoService.handleSignUp(email, password, email);
      localStorage.setItem("username", email);
      const user: User = {
        nit: nit,
        username: email,
        name: name,
        lastName: lastName,
        phone: phone.toString(),
        type: "client"
      }
      this.auth.createUser(user).subscribe(
        async response => {
          await this.cognitoService.handleSignUp(email, password, email);
          console.log('User saved:', response);
          const title = await this.traslateService.get('NOTIFI.titleSuccess').toPromise();
          const detail = await this.traslateService.get('NOTIFI.detailRegister').toPromise();
          this.notificationService.showStandard(title, detail)
          this.router.navigateByUrl("/account-confirmation");
        },
        async (error) => {
          const errorDetail = error?.error?.detailCode || 'Error';
          const title = await this.traslateService.get('NOTIFI.titleError').toPromise();
          const detail = await this.traslateService.get(`ERRORS.${errorDetail}`).toPromise();
          this.notificationService.showError(title, detail)
        }
      );
    } catch (error) {
      console.log('el error es: ', error)
      if (error instanceof AuthError) {
        switch (error.name) {
          case "UsernameExistsException":
            const title = await this.traslateService.get('NOTIFI.titleSuccess').toPromise();
            const detail = await this.traslateService.get('ERRORS.registeredUserError').toPromise();
            this.notificationService.showError(title, detail)
            break
          default:
            const detailB = await this.traslateService.get(`ERRORS.Error`).toPromise();
            this.notificationService.showError(title, detailB)
            break
        }
      } else {
        const title = await this.traslateService.get('NOTIFI.titleError').toPromise();
        const detailB = await this.traslateService.get(`ERRORS.Error`).toPromise();
        this.notificationService.showError(title, detailB)
      }
      this.loading = false;
    }
  }

  // Validador personalizado para verificar que las contraseñas coincidan
  passwordsMatchValidator(form: UntypedFormGroup) {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;

    // Si las contraseñas no coinciden, se marca el error
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }

  passwordsDoNotMatch(): boolean {
    const confirmPasswordControl = this.signupForm.get('confirmPassword');

    return this.signupForm.hasError('passwordsMismatch') &&
      !!confirmPasswordControl && confirmPasswordControl.touched;
  }

  /**
   * Password Hide/Show
   */
  toggleFieldTextType() {
    this.fieldTextType = !this.fieldTextType;
  }

  validatePass(password: string): boolean {
    //validamos la fortaleza de la contra, esta funcion lo utilizamos en insertar y modificar
    const passwordStrengthPattern: RegExp = /^(?=(.*\d){2})(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z\d]).{8,}$/;
    return passwordStrengthPattern.test(password);
  }

}
